/**
 * 邮件管理组件
 * 功能：邮件日志查看、批量发送邮件（含用户选择器和模板）
 */
import React, { useState, useEffect, useCallback } from 'react'
import {
    EnvelopeIcon,
    PaperAirplaneIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    UserGroupIcon,
    ClockIcon,
    EyeIcon,
    XMarkIcon,
    CheckIcon,
    UserIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// API Base URL
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.talknect.com'

interface EmailLog {
    id: number
    email_uuid: string
    to_email: string
    from_email: string
    subject: string
    content: string
    status: string
    error_message: string | null
    created_at: string
}

interface User {
    id: number
    email: string
    name?: string
    full_name?: string
    phone?: string
    role?: string
    created_at?: string
    avatar_url?: string
}

interface Pagination {
    page: number
    limit: number
    total: number
    total_pages: number
}

// Webhook 事件接口
interface WebhookEvent {
    id: number
    event_id: string
    event_type: string
    email_id: string | null
    to_email: string | null
    from_email: string | null
    subject: string | null
    payload: string
    is_read: number
    created_at: string
    received_at: string
}

// 事件类型配置
const EVENT_TYPE_CONFIG: Record<string, { label: string, color: string, bgColor: string }> = {
    'email.sent': { label: '已发送', color: 'text-blue-700', bgColor: 'bg-blue-100' },
    'email.delivered': { label: '已送达', color: 'text-green-700', bgColor: 'bg-green-100' },
    'email.bounced': { label: '退信', color: 'text-red-700', bgColor: 'bg-red-100' },
    'email.complained': { label: '投诉', color: 'text-orange-700', bgColor: 'bg-orange-100' },
    'email.opened': { label: '已打开', color: 'text-purple-700', bgColor: 'bg-purple-100' },
    'email.clicked': { label: '已点击', color: 'text-cyan-700', bgColor: 'bg-cyan-100' },
    'email.delivery_delayed': { label: '投递延迟', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
    'email.failed': { label: '发送失败', color: 'text-red-700', bgColor: 'bg-red-100' },
}

// 获取授权头
const getAuthHeaders = () => {
    const token = localStorage.getItem('token')
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    }
}

// 默认邮件模板
const DEFAULT_EMAIL_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>TalkNect</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f4f4f5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f4f4f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); padding: 32px; text-align: center;">
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 700;">TalkNect</h1>
              <p style="margin: 8px 0 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">全球语言学习平台</p>
            </td>
          </tr>
          
          <!-- Content -->
          <tr>
            <td style="padding: 40px 32px;">
              <h2 style="margin: 0 0 16px 0; color: #1f2937; font-size: 20px; font-weight: 600;">
                尊敬的用户，您好！
              </h2>
              
              <p style="margin: 0 0 16px 0; color: #4b5563; font-size: 15px; line-height: 1.6;">
                【在此输入您的邮件正文内容】
              </p>
              
              <p style="margin: 0 0 16px 0; color: #4b5563; font-size: 15px; line-height: 1.6;">
                感谢您选择 TalkNect，我们致力于为您提供优质的语言学习体验。
              </p>
              
              <!-- CTA Button (可选) -->
              <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 24px 0;">
                <tr>
                  <td style="background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); border-radius: 8px;">
                    <a href="https://talknect.com" target="_blank" style="display: inline-block; padding: 14px 28px; color: #ffffff; text-decoration: none; font-size: 15px; font-weight: 600;">
                      访问 TalkNect
                    </a>
                  </td>
                </tr>
              </table>
              
              <p style="margin: 24px 0 0 0; color: #6b7280; font-size: 14px;">
                此致<br>
                TalkNect 团队
              </p>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="background-color: #f9fafb; padding: 24px 32px; border-top: 1px solid #e5e7eb;">
              <p style="margin: 0; color: #9ca3af; font-size: 12px; text-align: center;">
                © 2024 TalkNect. All rights reserved.<br>
                <a href="https://talknect.com" style="color: #6366f1; text-decoration: none;">talknect.com</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`

export default function EmailManagement() {
    const [activeTab, setActiveTab] = useState<'logs' | 'batch' | 'events'>('logs')
    const [logs, setLogs] = useState<EmailLog[]>([])
    const [loading, setLoading] = useState(false)
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0
    })
    const [emailFilter, setEmailFilter] = useState('')

    // 批量发送状态
    const [selectedUsers, setSelectedUsers] = useState<User[]>([])
    const [subject, setSubject] = useState('')
    const [htmlContent, setHtmlContent] = useState(DEFAULT_EMAIL_TEMPLATE)
    const [sending, setSending] = useState(false)
    const [sendResult, setSendResult] = useState<any>(null)

    // 用户选择器状态
    const [users, setUsers] = useState<User[]>([])
    const [userSearch, setUserSearch] = useState('')
    const [usersLoading, setUsersLoading] = useState(false)
    const [showUserPicker, setShowUserPicker] = useState(false)

    // 用户详情弹窗
    const [detailUser, setDetailUser] = useState<User | null>(null)

    // 邮件详情弹窗
    const [detailEmail, setDetailEmail] = useState<EmailLog | null>(null)

    // Webhook 事件状态
    const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([])
    const [eventsLoading, setEventsLoading] = useState(false)
    const [eventsPagination, setEventsPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0
    })
    const [eventTypeFilter, setEventTypeFilter] = useState('')
    const [eventEmailFilter, setEventEmailFilter] = useState('')
    const [unreadCount, setUnreadCount] = useState(0)
    const [detailEvent, setDetailEvent] = useState<WebhookEvent | null>(null)

    // 加载邮件日志
    const loadLogs = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString()
            })
            if (emailFilter) {
                params.set('email', emailFilter)
            }

            const response = await fetch(`${API_BASE}/api/v1/admin/emails/logs?${params}`, {
                headers: getAuthHeaders()
            })
            const data = await response.json()

            if (data.success) {
                setLogs(data.data || [])
                setPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || 0,
                    total_pages: data.pagination?.total_pages || 0
                }))
            }
        } catch (error) {
            console.error('加载邮件日志失败:', error)
            toast.error('加载邮件日志失败')
        } finally {
            setLoading(false)
        }
    }, [pagination.page, pagination.limit, emailFilter])

    // 加载用户列表
    const loadUsers = useCallback(async () => {
        setUsersLoading(true)
        try {
            const params = new URLSearchParams({ limit: '50' })
            if (userSearch) {
                params.set('search', userSearch)
            }

            const response = await fetch(`${API_BASE}/api/v1/admin/users?${params}`, {
                headers: getAuthHeaders()
            })
            const data = await response.json()

            if (data.success) {
                setUsers(data.data || [])
            }
        } catch (error) {
            console.error('加载用户列表失败:', error)
        } finally {
            setUsersLoading(false)
        }
    }, [userSearch])

    // 加载 Webhook 事件
    const loadWebhookEvents = useCallback(async () => {
        setEventsLoading(true)
        try {
            const params = new URLSearchParams({
                page: eventsPagination.page.toString(),
                limit: eventsPagination.limit.toString()
            })
            if (eventTypeFilter) {
                params.set('event_type', eventTypeFilter)
            }
            if (eventEmailFilter) {
                params.set('email', eventEmailFilter)
            }

            const response = await fetch(`${API_BASE}/api/v1/admin/emails/webhook-events?${params}`, {
                headers: getAuthHeaders()
            })
            const data = await response.json()

            if (data.success) {
                setWebhookEvents(data.data || [])
                setEventsPagination(prev => ({
                    ...prev,
                    total: data.pagination?.total || 0,
                    total_pages: data.pagination?.total_pages || 0
                }))
            }
        } catch (error) {
            console.error('加载 Webhook 事件失败:', error)
            toast.error('加载事件失败')
        } finally {
            setEventsLoading(false)
        }
    }, [eventsPagination.page, eventsPagination.limit, eventTypeFilter, eventEmailFilter])

    // 加载未读统计
    const loadUnreadStats = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/api/v1/admin/emails/webhook-events/stats`, {
                headers: getAuthHeaders()
            })
            const data = await response.json()
            if (data.success) {
                setUnreadCount(data.data?.unread_count || 0)
            }
        } catch (error) {
            console.error('加载未读统计失败:', error)
        }
    }, [])

    // 标记事件为已读
    const markEventAsRead = async (eventId: number) => {
        try {
            await fetch(`${API_BASE}/api/v1/admin/emails/webhook-events/${eventId}/read`, {
                method: 'PUT',
                headers: getAuthHeaders()
            })
            // 更新本地状态
            setWebhookEvents(prev => prev.map(e => e.id === eventId ? { ...e, is_read: 1 } : e))
            setUnreadCount(prev => Math.max(0, prev - 1))
        } catch (error) {
            console.error('标记已读失败:', error)
        }
    }

    // 批量标记已读
    const markAllAsRead = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/v1/admin/emails/webhook-events/mark-all-read`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ event_type: eventTypeFilter || undefined })
            })
            const data = await response.json()
            if (data.success) {
                toast.success(data.message)
                loadWebhookEvents()
                loadUnreadStats()
            }
        } catch (error) {
            console.error('批量标记已读失败:', error)
            toast.error('操作失败')
        }
    }

    useEffect(() => {
        if (activeTab === 'logs') {
            loadLogs()
        }
    }, [activeTab, loadLogs])

    useEffect(() => {
        if (showUserPicker) {
            loadUsers()
        }
    }, [showUserPicker, loadUsers])

    useEffect(() => {
        if (activeTab === 'events') {
            loadWebhookEvents()
        }
    }, [activeTab, loadWebhookEvents])

    // 初始加载未读统计
    useEffect(() => {
        loadUnreadStats()
    }, [loadUnreadStats])

    // 选中/取消选中用户
    const toggleUserSelection = (user: User) => {
        setSelectedUsers(prev => {
            const exists = prev.find(u => u.id === user.id)
            if (exists) {
                return prev.filter(u => u.id !== user.id)
            } else {
                return [...prev, user]
            }
        })
    }

    // 移除已选用户
    const removeSelectedUser = (userId: number) => {
        setSelectedUsers(prev => prev.filter(u => u.id !== userId))
    }

    // 批量发送邮件
    const handleBatchSend = async () => {
        if (selectedUsers.length === 0) {
            toast.error('请至少选择一个收件人')
            return
        }
        if (!subject.trim()) {
            toast.error('请输入邮件主题')
            return
        }
        if (!htmlContent.trim()) {
            toast.error('请输入邮件内容')
            return
        }

        setSending(true)
        setSendResult(null)

        try {
            const recipientList = selectedUsers.map(u => ({
                email: u.email,
                name: u.full_name || u.name || ''
            }))

            const response = await fetch(`${API_BASE}/api/v1/admin/emails/batch-send`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    recipients: recipientList,
                    subject: subject.trim(),
                    customHtml: htmlContent
                })
            })
            const data = await response.json()

            if (data.success) {
                setSendResult(data.data)
                toast.success(data.message || '发送完成')
                setSelectedUsers([])
                setSubject('')
                setHtmlContent(DEFAULT_EMAIL_TEMPLATE)
            } else {
                toast.error(data.error?.message || '发送失败')
            }
        } catch (error) {
            console.error('批量发送失败:', error)
            toast.error('批量发送失败')
        } finally {
            setSending(false)
        }
    }

    // 格式化时间
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-'
        const date = new Date(dateStr)
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    // 获取用户显示名称（优先显示 full_name，适用于教师）
    const getUserDisplayName = (user: User) => {
        return user.full_name || user.name || user.email.split('@')[0]
    }

    return (
        <div className="p-6">
            {/* 标题 */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <EnvelopeIcon className="w-8 h-8 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">邮件管理</h1>
                        <p className="text-sm text-gray-500">查看邮件发送记录，批量发送邮件</p>
                    </div>
                </div>
            </div>

            {/* Tab 切换 */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setActiveTab('logs')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'logs'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <ClockIcon className="w-5 h-5 inline mr-2" />
                    发送记录
                </button>
                <button
                    onClick={() => setActiveTab('batch')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'batch'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <UserGroupIcon className="w-5 h-5 inline mr-2" />
                    批量发送
                </button>
                <button
                    onClick={() => setActiveTab('events')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors relative ${activeTab === 'events'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <EnvelopeIcon className="w-5 h-5 inline mr-2" />
                    邮件事件
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1.5">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            </div>

            {/* 邮件日志 Tab */}
            {activeTab === 'logs' && (
                <div>
                    {/* 搜索栏 */}
                    <div className="flex items-center gap-4 mb-4">
                        <div className="relative flex-1 max-w-md">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="搜索收件人邮箱..."
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && loadLogs()}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <button
                            onClick={loadLogs}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            搜索
                        </button>
                    </div>

                    {/* 日志表格 */}
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <EnvelopeIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>暂无邮件发送记录</p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">收件人</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">主题</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">发送时间</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {logs.map((log) => (
                                            <tr key={log.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900">{log.to_email}</div>
                                                    <div className="text-xs text-gray-500">From: {log.from_email}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-900 max-w-xs truncate">{log.subject || '(无主题)'}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {log.status === 'sent' ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                                            <CheckCircleIcon className="w-4 h-4" />
                                                            已发送
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                                            <XCircleIcon className="w-4 h-4" />
                                                            失败
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {formatDate(log.created_at)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => setDetailEmail(log)}
                                                        className="inline-flex items-center gap-1 px-3 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                                                    >
                                                        <EyeIcon className="w-4 h-4" />
                                                        查看详情
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* 分页 */}
                            <div className="flex items-center justify-between mt-4">
                                <p className="text-sm text-gray-500">
                                    共 {pagination.total} 条记录
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page <= 1}
                                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5" />
                                    </button>
                                    <span className="text-sm text-gray-600">
                                        第 {pagination.page} / {pagination.total_pages || 1} 页
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.total_pages}
                                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* 批量发送 Tab */}
            {activeTab === 'batch' && (
                <div className="max-w-4xl">
                    <div className="bg-white rounded-lg shadow p-6 space-y-6">
                        {/* 收件人选择 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                选择收件人 <span className="text-red-500">*</span>
                            </label>

                            {/* 已选用户显示 */}
                            {selectedUsers.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-3 p-3 bg-indigo-50 rounded-lg">
                                    {selectedUsers.map(user => (
                                        <span
                                            key={user.id}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-full text-sm text-indigo-700 border border-indigo-200"
                                        >
                                            <UserIcon className="w-4 h-4" />
                                            {getUserDisplayName(user)}
                                            <button
                                                onClick={() => removeSelectedUser(user.id)}
                                                className="ml-1 text-indigo-400 hover:text-indigo-600"
                                            >
                                                <XMarkIcon className="w-4 h-4" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* 用户选择器按钮 */}
                            <button
                                onClick={() => setShowUserPicker(!showUserPicker)}
                                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <UserGroupIcon className="w-5 h-5" />
                                {showUserPicker ? '收起用户列表' : '点击选择用户'}
                                <span className="text-sm text-gray-400">
                                    (已选 {selectedUsers.length} 人)
                                </span>
                            </button>

                            {/* 用户选择器面板 */}
                            {showUserPicker && (
                                <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                                    {/* 搜索框 */}
                                    <div className="p-3 bg-gray-50 border-b">
                                        <div className="relative">
                                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="搜索用户姓名或邮箱..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                        </div>
                                    </div>

                                    {/* 用户列表 */}
                                    <div className="max-h-64 overflow-y-auto">
                                        {usersLoading ? (
                                            <div className="flex justify-center py-8">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                            </div>
                                        ) : users.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500 text-sm">
                                                暂无用户数据
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {users.filter(u => u.email).map(user => {
                                                    const isSelected = selectedUsers.some(u => u.id === user.id)
                                                    return (
                                                        <div
                                                            key={user.id}
                                                            className={`flex items-center justify-between p-3 hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                                                                    {getUserDisplayName(user).charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{getUserDisplayName(user)}</p>
                                                                    <p className="text-xs text-gray-500">{user.email}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => setDetailUser(user)}
                                                                    className="px-3 py-1 text-xs text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                                                >
                                                                    <EyeIcon className="w-4 h-4 inline mr-1" />
                                                                    详情
                                                                </button>
                                                                <button
                                                                    onClick={() => toggleUserSelection(user)}
                                                                    className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${isSelected
                                                                        ? 'bg-indigo-600 text-white'
                                                                        : 'bg-gray-100 text-gray-700 hover:bg-indigo-100 hover:text-indigo-700'
                                                                        }`}
                                                                >
                                                                    {isSelected ? (
                                                                        <>
                                                                            <CheckIcon className="w-4 h-4" />
                                                                            已选
                                                                        </>
                                                                    ) : (
                                                                        '选中'
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* 主题 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                邮件主题 <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="请输入邮件主题"
                            />
                        </div>

                        {/* 内容 */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    邮件内容 (HTML) <span className="text-red-500">*</span>
                                </label>
                                <button
                                    onClick={() => setHtmlContent(DEFAULT_EMAIL_TEMPLATE)}
                                    className="text-xs text-indigo-600 hover:text-indigo-700"
                                >
                                    重置为默认模板
                                </button>
                            </div>
                            <textarea
                                value={htmlContent}
                                onChange={(e) => setHtmlContent(e.target.value)}
                                rows={16}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                                placeholder="输入 HTML 邮件内容..."
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                提示：修改模板中的【在此输入您的邮件正文内容】部分即可
                            </p>
                        </div>

                        {/* 发送按钮 */}
                        <div className="flex items-center justify-between pt-4 border-t">
                            <p className="text-sm text-gray-500">
                                将向 {selectedUsers.length} 位收件人发送邮件
                            </p>
                            <button
                                onClick={handleBatchSend}
                                disabled={sending || selectedUsers.length === 0}
                                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <PaperAirplaneIcon className="w-5 h-5" />
                                {sending ? '发送中...' : '发送邮件'}
                            </button>
                        </div>

                        {/* 发送结果 */}
                        {sendResult && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                <h4 className="font-medium text-gray-900 mb-2">发送结果</h4>
                                <p className="text-sm text-gray-600">
                                    总计: {sendResult.total} | 成功: <span className="text-green-600">{sendResult.success}</span> | 失败: <span className="text-red-600">{sendResult.failed}</span>
                                </p>
                                {sendResult.details && sendResult.details.some((d: any) => !d.success) && (
                                    <div className="mt-2">
                                        <p className="text-sm font-medium text-red-600">失败详情:</p>
                                        <ul className="text-xs text-red-500 mt-1">
                                            {sendResult.details.filter((d: any) => !d.success).map((d: any, i: number) => (
                                                <li key={i}>{d.email}: {d.error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 邮件事件 Tab */}
            {activeTab === 'events' && (
                <div>
                    {/* 筛选栏 */}
                    <div className="flex items-center gap-4 mb-4 flex-wrap">
                        <div className="relative flex-1 min-w-[200px] max-w-md">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="搜索收件人邮箱..."
                                value={eventEmailFilter}
                                onChange={(e) => setEventEmailFilter(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && loadWebhookEvents()}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <select
                            value={eventTypeFilter}
                            onChange={(e) => {
                                setEventTypeFilter(e.target.value)
                                setEventsPagination(prev => ({ ...prev, page: 1 }))
                            }}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="">全部类型</option>
                            {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>{config.label}</option>
                            ))}
                        </select>
                        <button
                            onClick={loadWebhookEvents}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            搜索
                        </button>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200"
                            >
                                全部标为已读
                            </button>
                        )}
                    </div>

                    {/* 事件列表 */}
                    {eventsLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : webhookEvents.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <EnvelopeIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>暂无邮件事件</p>
                            <p className="text-sm mt-1">请先在 Resend 控制台配置 Webhook 地址</p>
                        </div>
                    ) : (
                        <>
                            <div className="bg-white rounded-lg shadow overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">事件类型</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">收件人</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">主题</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">时间</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {webhookEvents.map((event) => {
                                            const config = EVENT_TYPE_CONFIG[event.event_type] || { label: event.event_type, color: 'text-gray-700', bgColor: 'bg-gray-100' }
                                            return (
                                                <tr key={event.id} className={`hover:bg-gray-50 ${event.is_read === 0 ? 'bg-indigo-50/30' : ''}`}>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
                                                            {event.is_read === 0 && <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></span>}
                                                            {config.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-900">{event.to_email || '-'}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-900 max-w-xs truncate">{event.subject || '(无主题)'}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        {formatDate(event.created_at)}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => {
                                                                setDetailEvent(event)
                                                                if (event.is_read === 0) {
                                                                    markEventAsRead(event.id)
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-1 px-3 py-1 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded transition-colors"
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                            查看详情
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* 分页 */}
                            <div className="flex items-center justify-between mt-4">
                                <p className="text-sm text-gray-500">
                                    共 {eventsPagination.total} 条记录
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setEventsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={eventsPagination.page <= 1}
                                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5" />
                                    </button>
                                    <span className="text-sm text-gray-600">
                                        第 {eventsPagination.page} / {eventsPagination.total_pages || 1} 页
                                    </span>
                                    <button
                                        onClick={() => setEventsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={eventsPagination.page >= eventsPagination.total_pages}
                                        className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* 用户详情弹窗 */}
            {detailUser && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-500 to-purple-600 text-white flex items-center justify-between">
                            <h2 className="text-lg font-semibold">用户详情</h2>
                            <button onClick={() => setDetailUser(null)} className="p-1 hover:bg-white/20 rounded">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                                    {getUserDisplayName(detailUser).charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{getUserDisplayName(detailUser)}</h3>
                                    <p className="text-gray-500">{detailUser.email}</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {detailUser.phone && (
                                    <div className="flex justify-between py-2 border-b">
                                        <span className="text-gray-500">手机号</span>
                                        <span className="text-gray-900">{detailUser.phone}</span>
                                    </div>
                                )}
                                {detailUser.role && (
                                    <div className="flex justify-between py-2 border-b">
                                        <span className="text-gray-500">角色</span>
                                        <span className="text-gray-900">{detailUser.role}</span>
                                    </div>
                                )}
                                {detailUser.created_at && (
                                    <div className="flex justify-between py-2 border-b">
                                        <span className="text-gray-500">注册时间</span>
                                        <span className="text-gray-900">{formatDate(detailUser.created_at)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 flex gap-3">
                                <button
                                    onClick={() => {
                                        toggleUserSelection(detailUser)
                                        setDetailUser(null)
                                    }}
                                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                >
                                    {selectedUsers.some(u => u.id === detailUser.id) ? '取消选中' : '选中此用户'}
                                </button>
                                <button
                                    onClick={() => setDetailUser(null)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                                >
                                    关闭
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 邮件详情弹窗 */}
            {detailEmail && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-500 to-purple-600 text-white flex items-center justify-between flex-shrink-0">
                            <h2 className="text-lg font-semibold">邮件详情</h2>
                            <button onClick={() => setDetailEmail(null)} className="p-1 hover:bg-white/20 rounded">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            {/* 基本信息 */}
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">收件人</span>
                                    <span className="text-gray-900">{detailEmail.to_email}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">发件人</span>
                                    <span className="text-gray-900">{detailEmail.from_email}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">邮件主题</span>
                                    <span className="text-gray-900">{detailEmail.subject || '(无主题)'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">发送时间</span>
                                    <span className="text-gray-900">{formatDate(detailEmail.created_at)}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">发送状态</span>
                                    {detailEmail.status === 'sent' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                            <CheckCircleIcon className="w-4 h-4" />
                                            发送成功
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                            <XCircleIcon className="w-4 h-4" />
                                            发送失败
                                        </span>
                                    )}
                                </div>
                                {detailEmail.error_message && (
                                    <div className="py-2 border-b">
                                        <span className="text-gray-500 font-medium block mb-1">错误信息</span>
                                        <span className="text-red-600 text-sm">{detailEmail.error_message}</span>
                                    </div>
                                )}
                            </div>

                            {/* 邮件内容 */}
                            <div>
                                <h3 className="text-gray-700 font-medium mb-3">邮件内容</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                    {(() => {
                                        let htmlContent = ''
                                        try {
                                            const parsed = JSON.parse(detailEmail.content)
                                            htmlContent = parsed.html || detailEmail.content
                                        } catch {
                                            htmlContent = detailEmail.content || '(无内容)'
                                        }
                                        return (
                                            <iframe
                                                srcDoc={htmlContent}
                                                className="w-full h-80 bg-white"
                                                sandbox="allow-same-origin"
                                                title="邮件内容预览"
                                            />
                                        )
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t flex-shrink-0">
                            <button
                                onClick={() => setDetailEmail(null)}
                                className="w-full px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Webhook 事件详情弹窗 */}
            {detailEvent && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b bg-gradient-to-r from-indigo-500 to-purple-600 text-white flex items-center justify-between flex-shrink-0">
                            <h2 className="text-lg font-semibold">事件详情</h2>
                            <button onClick={() => setDetailEvent(null)} className="p-1 hover:bg-white/20 rounded">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            {/* 基本信息 */}
                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">事件类型</span>
                                    {(() => {
                                        const config = EVENT_TYPE_CONFIG[detailEvent.event_type] || { label: detailEvent.event_type, color: 'text-gray-700', bgColor: 'bg-gray-100' }
                                        return (
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
                                                {config.label}
                                            </span>
                                        )
                                    })()}
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">收件人</span>
                                    <span className="text-gray-900">{detailEvent.to_email || '-'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">发件人</span>
                                    <span className="text-gray-900">{detailEvent.from_email || '-'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">邮件主题</span>
                                    <span className="text-gray-900">{detailEvent.subject || '(无主题)'}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">事件时间</span>
                                    <span className="text-gray-900">{formatDate(detailEvent.created_at)}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500 font-medium">接收时间</span>
                                    <span className="text-gray-900">{formatDate(detailEvent.received_at)}</span>
                                </div>
                            </div>

                            {/* 原始数据 */}
                            <div>
                                <h3 className="text-gray-700 font-medium mb-3">原始数据 (JSON)</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                                    <pre className="p-4 text-xs text-gray-700 overflow-x-auto max-h-64">
                                        {(() => {
                                            try {
                                                return JSON.stringify(JSON.parse(detailEvent.payload), null, 2)
                                            } catch {
                                                return detailEvent.payload
                                            }
                                        })()}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t flex-shrink-0">
                            <button
                                onClick={() => setDetailEvent(null)}
                                className="w-full px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
